import { TM_ORG_ROLES } from '@saucelabs/sl-util-config';
import { actions as notificationActions } from '@saucelabs/sl-container-notifications';
import { push } from 'connected-react-router';

import * as actions from './actions';
import { TUNNELS_POLLING_INTERVAL } from './constants';
import { actions as teamsActions } from './../teams';
import { operations as usersOperations } from './../users';

let tunnelSubscription;
export const subscribeTunnels = (username, userId) => (dispatch) => {
  if (tunnelSubscription) {
    return tunnelSubscription;
  }

  const instance = () => {
    dispatch(actions.getUserTunnels({ userId, username }, { skipErrorPage: true }));

    tunnelSubscription = setTimeout(() => instance(), TUNNELS_POLLING_INTERVAL);
    return tunnelSubscription;
  };

  return instance();
};

export const unsubscribeTunnels = () => () => {
  clearTimeout(tunnelSubscription);
  tunnelSubscription = null;
};

export const initialize = (id) => (dispatch) => (
  Promise.all([
    dispatch(actions.getUser(id)),
    dispatch(actions.getAccessKey(id)),
    dispatch(teamsActions.getTeamList()),
  ])
    .then(([{ data }]) => Promise.all([
      dispatch(actions.getUserActivity({ username: data.username })),
      dispatch(subscribeTunnels(data.username, data.id)),
    ]))
    .then(() => dispatch(actions.initialized()))
);

export const cleanup = () => (dispatch) => (
  Promise.all([
    dispatch(actions.cleanup()),
    dispatch(unsubscribeTunnels()),
  ])
);

export const resetPassword = (payload) => (dispatch) => (
  dispatch(actions.resetPassword(payload))
    .then(() => (
      dispatch(notificationActions.notifySuccess('Password reset instructions have been sent to user\'s email address.'))
    ))
    .catch(() => (
      dispatch(notificationActions.notifyDanger('There was problem resetting user\'s password.'))
    ))
);

export const updateUserTeam = (user, newTeam) => (dispatch) => {
  const teamChanged = user.teams.length && newTeam !== user.teams[0].id;
  let removeAction;
  let addAction;

  if (teamChanged) {
    removeAction = () => dispatch(actions.removeTeamMembership({
      organization: user.get('organization').id,
      team: user.get('teams')[0].id,
      user: user.get('id'),
    }));
  } else {
    removeAction = () => Promise.resolve();
  }

  if (newTeam && (teamChanged || !user.teams.length)) {
    addAction = () => dispatch(actions.addTeamMembership({
      organizationId: user.get('organization').id,
      teamId: newTeam,
      userId: user.get('id'),
    }));
  } else {
    addAction = () => Promise.resolve();
  }

  return removeAction().then(addAction);
};

export const changeAssignedTeams = (payload) => (dispatch) => {
  // Wrapper around old flow and new flow.
  // If teams are passed, we known that user has global teams enabled.
  // In other cases team will be passed and we will call legacy endpoints.
  const { user, team, teams } = payload;

  if (teams) {
    return dispatch(actions.changeAssignedTeams({ userId: user.id, teams }))
      .then(() => dispatch(actions.getUser(user.id)));
  }

  return dispatch(updateUserTeam(user, team));
};

export const updateUserInfo = ({
  user,
  first_name: firstName,
  last_name: lastName,
}) => (dispatch) => (
  dispatch(usersOperations.patchUser({
    userId: user.id, first_name: firstName, last_name: lastName,
  }))
    .then(() => dispatch(notificationActions.notifySuccess('User information changed.')))
);

export const updateRoleAndTeamsAssignment = ({ role, team, teams, user }) => (dispatch) => {
  const selectedRole = TM_ORG_ROLES.find((element) => element.role === Number(role));

  return Promise.all([
    dispatch(usersOperations.updateRole({ userId: user.id, role: selectedRole.type }))
      .catch(() => dispatch(notificationActions.notifyDanger(
        'Couldn\'t update user role',
      ))),
    dispatch(changeAssignedTeams({ user, team, teams }))
      .catch(() => dispatch(notificationActions.notifyDanger(
        'Couldn\'t update user team',
      ))),
  ])
    .then((responses) => {
      const anyRequestFailed = responses
        .filter((response) => response)
        .some((response) => (
          response?.payload?.category === 'danger'
        ));

      if (!anyRequestFailed) {
        dispatch(notificationActions.notifySuccess('User role and teams changed.'));
      }
    });
};

export const resetAccessKey = (payload) => (dispatch) => (
  dispatch(actions.resetAccessKey(payload))
    .then(() => (
      dispatch(notificationActions.notifySuccess('User\'s access token reset successfully.'))
    ))
    .catch(() => (
      dispatch(notificationActions.notifyDanger('There was problem resetting user\'s access key.'))
    ))
);

export const updatePassword = ({
  userId,
  password,
  password_confirm: confirmation,
}) => (dispatch) => (
  dispatch(actions.changePassword({ userId, password, verify_password: confirmation }))
    .then(() => dispatch(notificationActions.notifySuccess('Password changed.')))
);

export const deactivateUser = (userId) => (dispatch) => (
  dispatch(actions.deactivateUser(userId))
    .then(() => Promise.all([
      dispatch(notificationActions.notifySuccess('User deactivated.')),
      dispatch(push({ pathname: '/team-management/users/' })),
    ]))
    .catch(() => dispatch(notificationActions.notifyDanger(
      'Couldn\'t deactivate the user',
    )))
);

export const requestEmailChange = (payload) => (dispatch) => {
  const { user, ...rest } = payload;

  return dispatch(actions.changeEmail({ userId: user.id, ...rest }))
    .then(() => dispatch(notificationActions.notifySuccess(
      `Request to change an email to ${payload.email} has been sent`, null, true)),
    );
};
